import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { FIRM, PRACTICE_AREAS } from "@/data/site";
import Logo from "@/components/Logo";

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/practice-areas", label: "Practice" },
    { to: "/attorneys", label: "Attorneys" },
    { to: "/blog", label: "Journal" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "bg-[hsl(var(--ivory))]/90 backdrop-blur-md border-b hairline" : "bg-transparent"}`}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-brand">
            <Logo variant="full" textClassName="text-lg md:text-2xl" />
            <span className="mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--muted-foreground))] hidden md:inline pl-1">Est. 2017 Mumbai</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm uppercase tracking-[0.22em] editorial-link" data-testid={`nav-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href={`tel:${FIRM.phoneRaw}`} className="hidden md:inline-flex items-center gap-2 text-xs mono uppercase tracking-[0.22em]" data-testid="nav-call">
              <Phone className="h-3.5 w-3.5 text-gold" /> {FIRM.phone}
            </a>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 border hairline"
              aria-label="Open menu"
              data-testid="nav-open-menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/contact" className="hidden lg:inline-flex btn-ink !py-2.5 !px-5" data-testid="nav-consult">
              Book Consultation
            </Link>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-navy text-ivory"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-white/10">
              <div className="flex items-center gap-3">
              <Logo variant="mark" markClassName="h-8 w-8" />
              <span className="serif text-xl">Helkar <span className="text-gold">&</span> Associates</span>
            </div>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-close-menu">
                <X className="h-6 w-6" />
              </button>
            </div>
            <motion.div
              initial="hidden" animate="show"
              variants={{ show: { transition: { staggerChildren: 0.05 } }, hidden: {} }}
              className="px-6 md:px-10 py-10 space-y-6"
            >
              {links.map((l) => (
                <motion.div key={l.to} variants={{ hidden: { y: 20, opacity: 0 }, show: { y: 0, opacity: 1 } }}>
                  <Link to={l.to} className="serif text-4xl md:text-6xl block leading-none" data-testid={`mnav-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}

              <div className="pt-8 border-t border-white/10 space-y-3">
                <div className="mono text-xs uppercase tracking-[0.25em] text-ivory/60">Practice Areas</div>
                {PRACTICE_AREAS.map(p => (
                  <Link key={p.slug} to={`/practice-areas/${p.slug}`} className="flex items-baseline gap-3 text-sm">
                    {p.title}
                  </Link>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
